/* eslint-disable @next/next/link-passhref */
/* eslint-disable array-callback-return */
import React, { useMemo, useRef } from 'react';
import { Box, Typography } from '@mui/material';
import { add, getMonth, isAfter } from 'date-fns';
import moment from 'moment';
import { renderDuration } from '@/utils/renderDuration';
import { convertMoney } from '@/utils/convertMoney';
import { pr } from '@/utils/pxToRem';
import { style } from '@/styles/globals';
import SeoNote from '@/components/SeoNote';
import { styled } from '@mui/system';
import { lunarYear } from '@/utils/seoHelper';
import Link from 'next/link';
import { AIRLINES } from '@/containers/CompanyPage';
import { flatten, isEmpty, minBy } from 'lodash';
import { formatLocation } from '@/utils/formatLocation';
import { addCompanyToUrl } from '@/utils/addCompanyToUrl';
import { useRouter } from 'next/router';
import { useTrackIdUpdateLink } from '@/utils/useTrackIdUpdateLink';
import SeoNoteLine from '@/components/SeoNoteLine';
import { genLinkSeo } from '@/utils/genLinkSeo';

interface WrapperSectionProps {
  sxWrapper?: {
    [key: string]: any;
  };
  idComponent: string | undefined;
  routeName: string;
  routeName2: string;
  company: string;
  startPointHome: any;
  toPointHome: any;
  titleComponent: any;
  children: any;
  dataObj?: any;
  airlines?: string[];
  section: any;
  isHaveDivider: boolean;
  isDesktop?: boolean;
}
const REG = /from_name|to_name|luna_year|route_month|route_now|latest_pickup_time|earliest_pickup_time|distance|min_fare|min_7_days_fare|min_30_days_fare|route_airline|route_name|route_hyphen_name|average_duration|average_flight_count|company|min_price_time|route_url/gi;

const WrapperSection = (props: Partial<WrapperSectionProps>) => {
  const router = useRouter();
  const {
    sxWrapper,
    section,
    idComponent,
    routeName = '',
    routeName2,
    toPointHome,
    children,
    dataObj,
    airlines = [],
    titleComponent,
    startPointHome,
    company,
    isHaveDivider,
    isDesktop,
  } = props;

  const findLowestPrice = (airline: string, daysLimit = 0) => {
    if (dataObj && isEmpty(dataObj.min_flight_by_airline)) {
      return 0;
    }
    const flightByAirline = JSON.parse(
      JSON.stringify(dataObj.min_flight_by_airline),
    );
    if (daysLimit) {
      const maxDate = add(new Date(), { days: daysLimit });
      Object.keys(flightByAirline).forEach((airlineKey: string) => {
        flightByAirline[airlineKey] = flightByAirline[airlineKey].filter(
          (flight: any) => isAfter(new Date(flight.pickup_date), maxDate),
        );
      });
    }
    if (isEmpty(flightByAirline[airline])) {
      return 0;
    }
    if (airline) {
      return minBy(
        flightByAirline[airline],
        (flight: any) => flight.total_price_adult,
      ).total_price_adult;
    }
    const flattenValues = flatten(Object.values(flightByAirline)) as any;
    return minBy(flattenValues, (flight: any) => flight.total_price_adult)
      .total_price_adult;
  };

  const currMonth = getMonth(new Date());
  const companySlug = Object.keys(AIRLINES).find((key) => AIRLINES[key].name === company) || '';
  const params: { [key: string]: any } = {
    luna_year: lunarYear,
    route_month: currMonth + 1,
    route_now: moment().format('DD/MM/YYYY'),
    from_name: startPointHome?.location,
    to_name: toPointHome?.location,
    min_fare: companySlug
      ? convertMoney(findLowestPrice(AIRLINES[companySlug].type))
      : 0,
    min_7_days_fare: companySlug
      ? convertMoney(findLowestPrice(AIRLINES[companySlug].type, 7))
      : 0,
    min_30_days_fare: convertMoney(dataObj?.lowest_price || 0),
    latest_pickup_time: dataObj?.latest_pickup_time || '',
    earliest_pickup_time: dataObj?.earliest_pickup_time || '',
    distance: (dataObj.distance || 0).toLocaleString('vi-VN'),
    route_airline: airlines?.join(', ') || '',
    route_name: routeName,
    route_hyphen_name: routeName2,
    average_duration: renderDuration(dataObj?.average_duration || ''),
    average_flight_count: Math.round(
      dataObj.average_flight_count || 0,
    ).toLocaleString('vi-VN'),
    company,
    route_url: genLinkSeo({
      startName: startPointHome?.location,
      startStateName: startPointHome?.stateName,
      startId: startPointHome?.id,
      endName: toPointHome?.location,
      endStateName: toPointHome?.stateName,
      endId: toPointHome?.id,
      company: companySlug || company,
    }),
  };
  const replaceContent = (content: string) => content
    .replace(/({{|}})/g, '')
    .replace(REG, (x: string) => params[x] || '');

  const listNote = useMemo(() => {
    const list = [];
    if (section.note.vi) {
      if (Array.isArray(section.note.vi)) {
        section.note.vi.map((note: any) => {
          list.push(replaceContent(note));
        });
      } else {
        list.push(replaceContent(section.note.vi));
      }
    }
    if (section.note2.vi) {
      if (Array.isArray(section.note2.vi)) {
        section.note2.vi.map((note: any) => {
          list.push(replaceContent(note));
        });
      } else {
        list.push(replaceContent(section.note2.vi));
      }
    }
    return list;
  }, [props]);

  const ref = useRef(null) as any;
  useTrackIdUpdateLink(ref, idComponent);

  return (
    <>
      {isHaveDivider && section.id !== 'san-ve' && isDesktop ? (
        <StyledDividerDesktop />
      ) : isHaveDivider && section.id !== 'san-ve' && !isDesktop ? (
        <StyledDivider />
      ) : (
        <></>
      )}
      <Box
        sx={{

          ...sxWrapper,
          pt: isHaveDivider ? '8px' : 0,
        }}
        ref={ref}
        id={idComponent}
      >
        {!!section.title.vi && isHaveDivider ? (
          <Typography
            component={titleComponent}
            sx={{
              ...style.text.heading2,
              fontWeight: 500,
            }}
            color={style.color.black1}
          >
            {section.title.airline ? (
              <Link
                href={addCompanyToUrl(section.title.airline, router.asPath)}
              >
                <div
                  style={{
                    color: style.color.product3,
                    textDecoration: 'underline',
                  }}
                >
                  {replaceContent(section.title.vi)}
                </div>
              </Link>
            ) : (
              replaceContent(section.title.vi)
            )}
          </Typography>
        ) : (
          <Typography
            component={titleComponent}
            sx={{
              ...style.text.title,
              fontWeight: 500,
            }}
            color={style.color.black2}
          >
            {replaceContent(section.title.vi)}
          </Typography>
        )}
        {!!section.title2?.vi && (
          <Typography
            component="h3"
            sx={{ mt: pr`24px` }}
            variant="subtitle2"
            fontWeight={500}
            color={style.color.black2}
          >
            {replaceContent(section.title2.vi)}
          </Typography>
        )}
        {!!section.description.vi && (
          <div
            style={{
              ...style.text.content.regular,
              marginTop: pr`16px`,
              marginBottom: pr`8px`,
              color: style.color.black2,
            }}
            dangerouslySetInnerHTML={{
              __html: replaceContent(section.description.vi),
            }}
          />
        )}

        {children}

        {listNote.length > 1 ? (
          <Box sx={{ mt: pr`16px` }}>
            <SeoNote listNote={listNote} />
          </Box>
        ) : section.id === 'thong-tin' ? (
          <Typography
            variant="body2"
            sx={{ mt: pr`16px` }}
          >
            {listNote[0]}
          </Typography>
        ) : listNote.length === 1 ? (
          <Box sx={{ mt: pr`16px` }}>
            <SeoNoteLine message={listNote[0]} />
          </Box>
        ) : (
          <></>
        )}
      </Box>
    </>
  );
};

const StyledDivider = styled('div')`
  height: 8px;
  background: ${style.color.white2};
  width: calc(100% + 32px);
  margin-left: -16px !important;
`;

const StyledDividerDesktop = styled('div')`
  height: 24px;
  background: ${style.color.white2};
  width: calc(100% + 48px);
  margin-left: -24px !important;
`;

export default React.memo(WrapperSection);
